import { nonNarrowingError } from '@/lib/contacts/errors'
import type {
  CustomField,
  CustomFieldConversionPreview,
  CustomFieldNarrowingWarning,
  CustomFieldSetupForm,
  CustomFieldType,
} from '@/lib/contacts/types'
import { ConvertTypeDialog } from '@/pages/ConvertTypeDialog'
import { CustomFieldDialog } from '@/pages/CustomFieldDialog'
import { ManageChoicesDialog } from '@/pages/ManageChoicesDialog'
import { NarrowCustomFieldDialog } from '@/pages/NarrowCustomFieldDialog'

export type NarrowingConfirmation = {
  field: CustomField
  form: CustomFieldSetupForm
  warning: CustomFieldNarrowingWarning
}

export type ConversionConfirmation = {
  field: CustomField
  preview: CustomFieldConversionPreview | null
}

export type CustomFieldDialogsProps = {
  dialogOpen: boolean
  editingField: CustomField | null
  choicesField: CustomField | null
  choicesPending: boolean
  choicesError: Error | null
  conversion: ConversionConfirmation | null
  conversionPending: boolean
  conversionError: Error | null
  narrowingConfirmation: NarrowingConfirmation | null
  createPending: boolean
  createError: Error | null
  updatePending: boolean
  updateError: Error | null
  onCloseCreate: () => void
  onSubmitCreate: (form: CustomFieldSetupForm) => void
  onCloseEdit: () => void
  onSubmitUpdate: (field: CustomField, form: CustomFieldSetupForm) => void
  onManageChoices: (field: CustomField) => void
  onSubmitChoices: (field: CustomField, choices: string[]) => void
  onCloseChoices: () => void
  onConvertType: (field: CustomField) => void
  onCancelConversion: () => void
  onPreviewConversion: (field: CustomField, fieldType: CustomFieldType) => void
  onConfirmConversion: (field: CustomField, fieldType: CustomFieldType) => void
  onCancelNarrowing: () => void
  onConfirmNarrowing: () => void
}

export function CustomFieldDialogs({
  dialogOpen,
  editingField,
  choicesField,
  choicesPending,
  choicesError,
  conversion,
  conversionPending,
  conversionError,
  narrowingConfirmation,
  createPending,
  createError,
  updatePending,
  updateError,
  onCloseCreate,
  onSubmitCreate,
  onCloseEdit,
  onSubmitUpdate,
  onManageChoices,
  onSubmitChoices,
  onCloseChoices,
  onConvertType,
  onCancelConversion,
  onPreviewConversion,
  onConfirmConversion,
  onCancelNarrowing,
  onConfirmNarrowing,
}: CustomFieldDialogsProps) {
  return (
    <>
      {dialogOpen && (
        <CustomFieldDialog
          mode="new"
          pending={createPending}
          error={createError}
          onClose={onCloseCreate}
          onSubmit={onSubmitCreate}
        />
      )}
      {editingField && (
        <CustomFieldDialog
          key={editingField.id}
          mode="edit"
          field={editingField}
          pending={updatePending}
          error={nonNarrowingError(updateError)}
          onClose={onCloseEdit}
          onSubmit={(form) => onSubmitUpdate(editingField, form)}
          onConvertType={() => onConvertType(editingField)}
          onManageChoices={() => onManageChoices(editingField)}
        />
      )}
      {choicesField && (
        <ManageChoicesDialog
          field={choicesField}
          pending={choicesPending}
          error={choicesError}
          onClose={onCloseChoices}
          onSubmit={(choices) => onSubmitChoices(choicesField, choices)}
        />
      )}
      {conversion && (
        <ConvertTypeDialog
          field={conversion.field}
          preview={conversion.preview}
          pending={conversionPending}
          error={conversionError}
          onClose={onCancelConversion}
          onPreview={(fieldType) => onPreviewConversion(conversion.field, fieldType)}
          onConfirm={(fieldType) => onConfirmConversion(conversion.field, fieldType)}
        />
      )}
      {narrowingConfirmation && (
        <NarrowCustomFieldDialog
          field={narrowingConfirmation.field}
          form={narrowingConfirmation.form}
          warning={narrowingConfirmation.warning}
          pending={updatePending}
          onCancel={onCancelNarrowing}
          onConfirm={onConfirmNarrowing}
        />
      )}
    </>
  )
}
